import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import FormTabs from '../common/FormTabs';
import DateTimePicker from '../common/DateTimePicker';
import { 
  InvoiceRequest, 
  ProformaRequest, 
  InvoiceItem 
} from '../../types/billing';
import { Product } from '../../types/product';
import { Customer } from '../../types/customer';
import { useCustomers } from '../../hooks/useCustomers';
import { useProducts } from '../../hooks/useProducts';
import { usePaymentOptions } from '../../hooks/usePaymentOptions';
import {
  XMarkIcon,
  PlusIcon,
  TagIcon,
  TrashIcon,
  UserIcon,
  CurrencyDollarIcon,
  MagnifyingGlassIcon,
  BuildingOffice2Icon,
  DocumentTextIcon,
  CalculatorIcon,
  ShieldExclamationIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import Modal from '../common/Modal';

interface InvoiceFormData {
  documentType: 'INVOICE' | 'PROFORMA';
  selectedCustomer?: Customer | null;
  buyerName: string;
  buyerEmail: string;
  buyerPhone: string;
  buyerTaxNumber: string;
  buyerStreet: string;
  buyerCity: string;
  buyerPostalCode: string;
  buyerCountry: string;
  items: InvoiceItem[];
  paymentMethod: string;
  paymentDeadline: number;
  currency: string;
  notes: string;
  // Proforma specifikus mezők
  validUntil?: Date;
  convertToInvoice?: boolean;
  // Garancia beállítás
  generateWarranty?: boolean;
}

interface InvoiceFormProps {
  documentType: 'INVOICE' | 'PROFORMA';
  onSubmit: (data: InvoiceRequest | ProformaRequest) => void;
  onClose: () => void;
  isOpen: boolean;
  loading?: boolean;
  initialData?: any; // Szerkesztéshez meglévő adat
}

const InvoiceForm: React.FC<InvoiceFormProps> = ({ 
  documentType, 
  onSubmit, 
  onClose, 
  isOpen,
  loading = false,
  initialData
}) => {
  const { customers } = useCustomers();
  const { products } = useProducts();
  const { paymentOptions } = usePaymentOptions();
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [productSearchTerm, setProductSearchTerm] = useState('');

  const [activeTab, setActiveTab] = useState<'customer' | 'items' | 'payment'>('customer');
  const panel = (id: 'customer' | 'items' | 'payment') => (activeTab === id ? 'space-y-5' : 'hidden');
  const { register, handleSubmit, watch, setValue, reset, getValues } = useForm<InvoiceFormData>({
    defaultValues: {
      documentType,
      selectedCustomer: initialData?.selectedCustomer || null,
      buyerName: initialData?.buyer?.name || initialData?.buyerName || '',
      buyerEmail: initialData?.buyer?.email || initialData?.buyerEmail || '',
      buyerPhone: initialData?.buyer?.phone || initialData?.buyerPhone || '',
      buyerTaxNumber: initialData?.buyer?.taxNumber || initialData?.buyerTaxNumber || '',
      buyerStreet: initialData?.buyer?.address?.street || (typeof initialData?.buyerAddress === 'object' ? initialData?.buyerAddress?.street : String(initialData?.buyerAddress || '').split(',')[0]?.trim()) || '',
      buyerCity: initialData?.buyer?.address?.city || (typeof initialData?.buyerAddress === 'object' ? initialData?.buyerAddress?.city : String(initialData?.buyerAddress || '').split(',')[1]?.trim()) || '',
      buyerPostalCode: initialData?.buyer?.address?.postalCode || (typeof initialData?.buyerAddress === 'object' ? initialData?.buyerAddress?.postalCode : String(initialData?.buyerAddress || '').split(',')[2]?.trim()) || '',
      buyerCountry: initialData?.buyer?.address?.country || (typeof initialData?.buyerAddress === 'object' ? initialData?.buyerAddress?.country : '') || 'Magyarország',
      items: initialData?.items || [],
      paymentMethod: initialData?.paymentMethod || 'TRANSFER',
      paymentDeadline: initialData?.paymentDeadline || 8,
      currency: initialData?.currency || 'HUF',
      notes: initialData?.notes || '',
      validUntil: initialData?.validUntil ? new Date(initialData.validUntil.seconds ? initialData.validUntil.toDate() : initialData.validUntil) : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      convertToInvoice: initialData?.convertToInvoice || false,
      generateWarranty: initialData?.generateWarranty || false
    }
  });

  const selectedCustomer = watch('selectedCustomer');
  const items = watch('items') || [];
  const paymentMethod = watch('paymentMethod');

  // Automatikus fizetési határidő beállítása fizetési mód alapján
  useEffect(() => {
    if (paymentMethod === 'CASH') {
      setValue('paymentDeadline', 0);
    } else if (paymentMethod === 'CARD') {
      setValue('paymentDeadline', 0);
    } else if (paymentMethod === 'ONLINE') {
      setValue('paymentDeadline', 0);
    } else if (paymentMethod === 'COD') {
      setValue('paymentDeadline', 0);
    } else {
      // Átutalás és egyéb esetekben alapértelmezett 8 nap
      if (getValues('paymentDeadline') === 0) {
        setValue('paymentDeadline', 8);
      }
    }
  }, [paymentMethod, setValue, getValues]);

  // Ügyfél kiválasztása
  const handleCustomerSelect = (customer: Customer) => {
    setValue('selectedCustomer', customer);
    const customerName = customer.type === 'organization' 
      ? customer.companyName 
      : `${customer.firstName} ${customer.lastName}`;
    
    setValue('buyerName', customerName);
    setValue('buyerEmail', customer.email || '');
    setValue('buyerPhone', customer.phone || '');
    setValue('buyerTaxNumber', customer.taxNumber || '');
    setValue('buyerStreet', customer.address?.street || '');
    setValue('buyerCity', customer.address?.city || '');
    setValue('buyerPostalCode', customer.address?.postalCode || '');
    setValue('buyerCountry', customer.address?.country || 'Magyarország');
    
    setShowCustomerSearch(false);
    setCustomerSearchTerm('');
    toast.success('Ügyfél kiválasztva');
  };

  // Termék hozzáadása
  const handleProductSelect = (product: Product) => {
    const taxRate = typeof product.taxRate === 'string' ? 
      parseFloat(product.taxRate.replace('%', '')) : 
      (product.taxRate || 27);
    
    const unitPrice = product.netPrice || 0;
    const quantity = 1;
    const netValue = quantity * unitPrice;
    const taxValue = netValue * (taxRate / 100);
    const grossValue = netValue + taxValue;
    
    const newItem: InvoiceItem = {
      name: product.name,
      quantity: quantity,
      unit: product.unit || 'db',
      unitPrice: unitPrice,
      taxRate: isNaN(taxRate) ? 27 : taxRate,
      netValue: isNaN(netValue) ? 0 : netValue,
      taxValue: isNaN(taxValue) ? 0 : taxValue,
      grossValue: isNaN(grossValue) ? 0 : grossValue,
      comment: product.description || ''
    };
    
    setValue('items', [...items, newItem]);
    setShowProductSearch(false);
    setProductSearchTerm('');
    toast.success('Termék hozzáadva');
  };

  // Tétel frissítése
  const updateItem = (index: number, field: keyof InvoiceItem, value: any) => {
    const updatedItems = [...items];
    
    // Biztosítjuk, hogy a numerikus értékek ne legyenek NaN
    if (field === 'quantity' || field === 'unitPrice' || field === 'taxRate') {
      const numValue = Number(value);
      value = isNaN(numValue) ? 0 : numValue;
    }
    
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };

    // Automatikus kalkuláció
    if (field === 'quantity' || field === 'unitPrice' || field === 'taxRate') {
      const item = updatedItems[index];
      const quantity = isNaN(item.quantity) ? 0 : item.quantity;
      const unitPrice = isNaN(item.unitPrice) ? 0 : item.unitPrice;
      const taxRate = isNaN(item.taxRate) ? 0 : item.taxRate;
      
      item.netValue = quantity * unitPrice;
      item.taxValue = item.netValue * (taxRate / 100);
      item.grossValue = item.netValue + item.taxValue;
      
      // Biztosítjuk, hogy a számított értékek se legyenek NaN
      item.netValue = isNaN(item.netValue) ? 0 : item.netValue;
      item.taxValue = isNaN(item.taxValue) ? 0 : item.taxValue;
      item.grossValue = isNaN(item.grossValue) ? 0 : item.grossValue;
    }

    setValue('items', updatedItems);
  };

  // Tétel törlése
  const removeItem = (index: number) => {
    const updatedItems = items.filter((item, i) => i !== index);
    setValue('items', updatedItems);
  };

  // Összesítők számítása
  const calculateTotals = () => {
    return items.reduce(
      (totals, item) => {
        const netValue = (item.netValue != null && !isNaN(item.netValue)) ? item.netValue : 0;
        const taxValue = (item.taxValue != null && !isNaN(item.taxValue)) ? item.taxValue : 0;
        const grossValue = (item.grossValue != null && !isNaN(item.grossValue)) ? item.grossValue : 0;
        
        return {
          netTotal: totals.netTotal + netValue,
          taxTotal: totals.taxTotal + taxValue,
          grossTotal: totals.grossTotal + grossValue
        };
      },
      { netTotal: 0, taxTotal: 0, grossTotal: 0 }
    );
  };

  // Form submit
  const onSubmitHandler = (data: InvoiceFormData) => {
    if (!data.buyerName.trim()) {
      toast.error('Vevő neve kötelező!');
      return;
    }

    if (data.items.length === 0) {
      toast.error('Legalább egy tétel szükséges!');
      return;
    }

    const invoiceData: InvoiceRequest = {
      buyer: {
        name: data.buyerName,
        email: data.buyerEmail,
        phone: data.buyerPhone,
        taxNumber: data.buyerTaxNumber,
        address: {
          street: data.buyerStreet,
          city: data.buyerCity,
          postalCode: data.buyerPostalCode,
          country: data.buyerCountry
        }
      },
      items: data.items,
      paymentMethod: data.paymentMethod,
      paymentDeadline: data.paymentDeadline,
      currency: data.currency,
      notes: data.notes,
      organizationId: '', // Ez a submit-ban fog beállításra kerülni
      generateWarranty: data.generateWarranty || false,
      customerId: data.selectedCustomer?.id || initialData?.customerId || undefined,
      sourceType: initialData?.sourceType || undefined,
      sourceId: initialData?.sourceId || undefined,
      sourceNumber: initialData?.sourceNumber || undefined
    };

    if (documentType === 'PROFORMA') {
      const proforma: ProformaRequest = {
        ...invoiceData,
        validUntil: data.validUntil!,
        convertToInvoice: data.convertToInvoice || false
      };
      onSubmit(proforma);
    } else {
      onSubmit(invoiceData);
    }
  };

  // Szűrt ügyfelek
  const filteredCustomers = customers.filter(customer => {
    const searchLower = customerSearchTerm.toLowerCase();
    const customerName = customer.type === 'organization' 
      ? customer.companyName 
      : `${customer.firstName} ${customer.lastName}`;
    
    return (
      customerName.toLowerCase().includes(searchLower) ||
      customer.email.toLowerCase().includes(searchLower) ||
      customer.taxNumber?.toLowerCase().includes(searchLower)
    );
  }).slice(0, 10);

  // Szűrt termékek
  const filteredProducts = products.filter(product => {
    const searchLower = productSearchTerm.toLowerCase();
    return (
      product.name.toLowerCase().includes(searchLower) ||
      product.description?.toLowerCase().includes(searchLower) ||
      product.code?.toLowerCase().includes(searchLower)
    );
  }).slice(0, 10);

  const formatCurrency = (amount: number) => {
    if (isNaN(amount) || amount === null || amount === undefined) {
      return '0 Ft';
    }
    return new Intl.NumberFormat('hu-HU').format(Math.round(amount)) + ' Ft';
  };

  const totals = calculateTotals();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        initialData 
          ? (documentType === 'INVOICE' ? 'Számla vázlat szerkesztése' : 'Díjbekérő vázlat szerkesztése')
          : (documentType === 'INVOICE' ? 'Új számla vázlat létrehozása' : 'Új díjbekérő vázlat létrehozása')
      }
      size="5xl"
      noPadding
    >
      <form onSubmit={handleSubmit(onSubmitHandler)} noValidate className="flex flex-col min-h-full">
        <div className="px-4 sm:px-6 flex-1 pb-5">
        <div className="sticky top-0 z-10 bg-[var(--crm-surface)] pt-3 sm:pt-4">
          <FormTabs
            sticky={false}
            tabs={[
              { id: 'customer', label: 'Vevő', icon: UserIcon },
              { id: 'items', label: `Tételek${items.length ? ` (${items.length})` : ''}`, icon: TagIcon },
              { id: 'payment', label: 'Fizetés, megjegyzés', icon: CurrencyDollarIcon },
            ]}
            active={activeTab}
            onChange={(id) => setActiveTab(id as 'customer' | 'items' | 'payment')}
          />
        </div>
        <div className="pt-5">
        <div className={panel('customer')}>
        {/* Vevő kiválasztása / adatok */}
        <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-600">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <UserIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Vevő adatok</h3>
          </div>
          <div className="p-4">
              
              {/* Ügyfél keresés */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowCustomerSearch(!showCustomerSearch)}
                  className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-left hover:border-gray-400 dark:hover:border-gray-500 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 bg-white dark:bg-gray-700"
                >
                  {selectedCustomer ? (
                    <div className="flex items-center space-x-2">
                      {selectedCustomer.type === 'organization' ? (
                        <BuildingOffice2Icon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      ) : (
                        <UserIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      )}
                      <span className="text-sm text-gray-900 dark:text-gray-100">
                        {selectedCustomer.type === 'organization' 
                          ? selectedCustomer.companyName 
                          : `${selectedCustomer.firstName} ${selectedCustomer.lastName}`}
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-500 dark:text-gray-400 text-sm">Válasszon ügyfelet vagy adja meg manuálisan...</span>
                  )}
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                </button>

                {showCustomerSearch && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-80 overflow-hidden">
                    <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                      <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                        <input
                          type="text"
                          placeholder="Keresés név, email vagy adószám alapján..."
                          value={customerSearchTerm}
                          onChange={(e) => setCustomerSearchTerm(e.target.value)}
                          className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          autoFocus
                        />
                      </div>
                    </div>
                    
                    <div className="max-h-60 overflow-y-auto">
                      {filteredCustomers.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 dark:text-gray-400 text-sm">
                          Nincs találat
                        </div>
                      ) : (
                        filteredCustomers.map((customer) => (
                          <button
                            key={customer.id}
                            type="button"
                            onClick={() => handleCustomerSelect(customer)}
                            className="w-full px-3 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                          >
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0 mt-0.5">
                                {customer.type === 'organization' ? (
                                  <BuildingOffice2Icon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                ) : (
                                  <UserIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {customer.type === 'organization' 
                                    ? customer.companyName 
                                    : `${customer.firstName} ${customer.lastName}`}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {customer.email}
                                  {customer.taxNumber && ` • ${customer.taxNumber}`}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Manuális beviteli mezők */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Név *
                  </label>
                  <input
                    type="text"
                    {...register('buyerName')}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-100 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder="Vevő neve"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    {...register('buyerEmail')}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-100 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder="email@pelda.hu"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    {...register('buyerPhone')}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-100 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder="+36 30 123 4567"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Adószám
                  </label>
                  <input
                    type="text"
                    {...register('buyerTaxNumber')}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-100 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder="12345678-1-23"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Irányítószám
                  </label>
                  <input
                    type="text"
                    {...register('buyerPostalCode')}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-100 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder="1234"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Város
                  </label>
                  <input
                    type="text"
                    {...register('buyerCity')}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-100 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder="Budapest"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Utca, házszám
                  </label>
                  <input
                    type="text"
                    {...register('buyerStreet')}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-100 
                             placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    placeholder="Főutca 123."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        </div>
        <div className={panel('items')}>
        {/* Tételek */}
        <div className="crm-card">
          <div className="flex">
            <div className="p-4 flex-1">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50 flex items-center gap-2"><TagIcon className="w-4 h-4 text-gray-400" />Tételek</h3>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowProductSearch(true)}
                    className="crm-btn crm-btn-primary text-sm"
                  >
                    <TagIcon className="h-4 w-4 mr-2" />
                    Termék választása
                  </button>
                  <button
                    type="button"
                    onClick={() => setValue('items', [...items, {
                      name: '',
                      quantity: 1,
                      unit: 'db',
                      unitPrice: 0,
                      taxRate: 27,
                      netValue: 0,
                      taxValue: 0,
                      grossValue: 0,
                      comment: ''
                    }])}
                    className="bg-primary-600 text-white px-3 py-2 rounded-lg hover:bg-primary-700 transition-colors flex items-center text-sm"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Új tétel
                  </button>
                </div>
              </div>

              {items.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <TagIcon className="mx-auto h-12 w-12 text-gray-400 mb-2" />
                  <p>Még nincsenek tételek hozzáadva</p>
                  <p className="text-sm">Használja a fenti gombokat tételek hozzáadásához</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((item, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Megnevezés *
                          </label>
                          <input
                            type="text"
                            value={item.name}
                            onChange={(e) => updateItem(index, 'name', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                            placeholder="Termék/szolgáltatás neve"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Mennyiség *
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={isNaN(item.quantity) ? 0 : item.quantity}
                            onChange={(e) => updateItem(index, 'quantity', Number(e.target.value))}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Egység
                          </label>
                          <select
                            value={item.unit || 'db'}
                            onChange={(e) => updateItem(index, 'unit', e.target.value)}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                          >
                            <option value="db">db</option>
                            <option value="óra">óra</option>
                            <option value="alkalom">alkalom</option>
                            <option value="m">m</option>
                            <option value="kg">kg</option>
                            <option value="liter">liter</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Egységár (Ft) *
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={isNaN(item.unitPrice) ? 0 : item.unitPrice}
                            onChange={(e) => updateItem(index, 'unitPrice', Number(e.target.value))}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>

                        <div className="flex items-end justify-between">
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              ÁFA %
                            </label>
                            <select
                              value={isNaN(item.taxRate) ? 27 : item.taxRate}
                              onChange={(e) => updateItem(index, 'taxRate', Number(e.target.value))}
                              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                            >
                              <option value={0}>0%</option>
                              <option value={5}>5%</option>
                              <option value={18}>18%</option>
                              <option value={27}>27%</option>
                            </select>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="ml-2 text-red-600 hover:text-red-700 p-1 rounded hover:bg-red-50"
                            title="Tétel törlése"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Nettó: </span>
                            <span className="font-medium">{formatCurrency(item.netValue || 0)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">ÁFA: </span>
                            <span className="font-medium">{formatCurrency(item.taxValue || 0)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Bruttó: </span>
                            <span className="font-medium">{formatCurrency(item.grossValue || 0)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Összesítő */}
        {items.length > 0 && (
          <div className="crm-card">
            <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <CalculatorIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Összesítő</h3>
          </div>
          <div className="p-4">
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(totals.netTotal)}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Nettó összeg</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatCurrency(totals.taxTotal)}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">ÁFA összeg</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-primary-600 dark:text-primary-400">{formatCurrency(totals.grossTotal)}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Bruttó összeg</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        </div>
        <div className={panel('payment')}>
        {/* Fizetési beállítások */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <CurrencyDollarIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Fizetési beállítások</h3>
          </div>
          <div className="p-4">
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Fizetési mód
                  </label>
                  <select
                    {...register('paymentMethod')}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-100 
                             focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  >
                    {paymentOptions?.paymentMethods.map((method) => (
                      <option key={method.code} value={method.code}>
                        {method.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Fizetési határidő
                  </label>
                  <div className="space-y-2">
                    <select
                      onChange={(e) => {
                        const value = parseInt(e.target.value);
                        if (!isNaN(value)) {
                          setValue('paymentDeadline', value);
                        }
                      }}
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-100 
                               focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    >
                      <option value="">Válasszon előre definiált opciót...</option>
                      {paymentOptions?.paymentDeadlineOptions.map((option) => (
                        <option key={option.days} value={option.days}>
                          {option.name} ({option.days} nap)
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min="0"
                      max="365"
                      {...register('paymentDeadline')}
                      placeholder="Vagy adja meg egyéni napok számát"
                      className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-100 
                               placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Pénznem
                  </label>
                  <select
                    {...register('currency')}
                    className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-100 
                             focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  >
                    {paymentOptions?.currencies.map((currency) => (
                      <option key={currency.code} value={currency.code}>
                        {currency.code} - {currency.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Díjbekérő specifikus beállítások */}
              {documentType === 'PROFORMA' && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Érvényesség vége
                      </label>
                      <DateTimePicker showTime={false} value={watch('validUntil') instanceof Date ? watch('validUntil') : (watch('validUntil') ? new Date(watch('validUntil') as any) : undefined)} onChange={(d) => setValue('validUntil', d)} placeholder="Válassz dátumot" />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="convertToInvoice"
                        {...register('convertToInvoice')}
                        className="rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 dark:bg-gray-700"
                      />
                      <label htmlFor="convertToInvoice" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Automatikus számla konvertálás
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Garancia generálás opció */}
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3 p-3 bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700 rounded-lg">
                  <ShieldExclamationIcon className="h-5 w-5 text-green-600 dark:text-green-300 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="generateWarranty"
                        {...register('generateWarranty')}
                        className="rounded border-gray-300 dark:border-gray-600 text-green-600 focus:ring-green-500 dark:bg-gray-700"
                      />
                      <label htmlFor="generateWarranty" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                        Garancia papír generálása
                      </label>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Automatikusan létrehozza a garancia papírt a termékekhez az eladási adatokkal
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Megjegyzés
                </label>
                <textarea
                  {...register('notes')}
                  rows={3}
                  className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-700 dark:text-gray-100 
                           placeholder-gray-500 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  placeholder="Opcionális megjegyzés a számlához..."
                />
              </div>
            </div>
          </div>
        </div>

        </div>
        </div>
        </div>
        {/* Akció gombok */}
        <div className="sticky bottom-0 z-10 flex justify-end gap-3 px-4 sm:px-6 py-3 border-t border-[var(--crm-border)] bg-[var(--crm-surface)]">
          <button type="button" onClick={onClose} className="crm-btn crm-btn-secondary">
            Mégse
          </button>
          <button type="submit" disabled={loading || items.length === 0} className="crm-btn crm-btn-primary">
            {loading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : null}
            {initialData 
              ? (documentType === 'INVOICE' ? 'Számla vázlat frissítése' : 'Díjbekérő vázlat frissítése')
              : (documentType === 'INVOICE' ? 'Számla vázlat mentése' : 'Díjbekérő vázlat mentése')
            }
          </button>
        </div>

        {/* Termék keresés modal */}
        {showProductSearch && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden">
              <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Termék választása</h3>
                <button
                  type="button"
                  onClick={() => setShowProductSearch(false)}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="p-4">
                <div className="relative mb-4">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    placeholder="Keresés termék név, leírás vagy kód alapján..."
                    value={productSearchTerm}
                    onChange={(e) => setProductSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    autoFocus
                  />
                </div>

                <div className="max-h-80 overflow-y-auto space-y-2">
                  {filteredProducts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      Nincs találat
                    </div>
                  ) : (
                    filteredProducts.map((product) => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => handleProductSelect(product)}
                        className="w-full p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg"
                      >
                        <div className="font-medium text-gray-900 dark:text-gray-100">{product.name}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">{product.description}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex justify-between">
                          <span>Kód: {product.code}</span>
                          <span>{formatCurrency(product.netPrice || 0)}</span>
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

      </form>
    </Modal>
  );
};

export default InvoiceForm; 